import { createContext, useContext, useRef } from "react";

import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";

import { NodeExecutionMetadata } from "src/api/types";
import { ResultDataAndAuxRowV2 } from "src/dataTable/types";

/*
 ** This store is not supposed to use for graph related data.
 ** It is only for Authoring Page UI related data.
 */

type AuthoringUIState = {
  selectedResultsRow: ResultDataAndAuxRowV2 | null;
  selectedResultsRowNodeId: string | null;
  isNodeEditorOpen: boolean;
};

type AuthoringUIActions = {
  setSelectedResultsRow: (
    row: ResultDataAndAuxRowV2 | null,
    nodeId: string | null,
  ) => void;
  enrichSelectedResultsRowMetadata: (metadata: NodeExecutionMetadata) => void;
  setNodeEditorOpen: (show: boolean) => void;
};

type AuthoringUIStore = ZustandStore<AuthoringUIState, AuthoringUIActions>;

const createAuthoringUIStore = () => {
  return createStore(
    immer<AuthoringUIStore>((set) => ({
      selectedResultsRow: null,
      selectedResultsRowNodeId: null,
      isNodeEditorOpen: false,
      actions: {
        setSelectedResultsRow: (
          row: ResultDataAndAuxRowV2 | null,
          nodeId: string | null,
        ) =>
          set((state) => {
            state.selectedResultsRow = row;
            state.selectedResultsRowNodeId = nodeId;
          }),
        enrichSelectedResultsRowMetadata: (metadata: NodeExecutionMetadata) =>
          set((state) => {
            if (state.selectedResultsRow) {
              state.selectedResultsRow.nodeExecutionMetadata = metadata;
            }
          }),
        setNodeEditorOpen: (show: boolean) =>
          set((state) => {
            state.isNodeEditorOpen = show;
          }),
      },
    })),
  );
};
type AuthoringUIContextType = ReturnType<typeof createAuthoringUIStore>;

const AuthoringUIContext = createContext<AuthoringUIContextType | null>(null);

export const AuthoringUIProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const storeRef = useRef<AuthoringUIContextType>(undefined);

  if (!storeRef.current) {
    storeRef.current = createAuthoringUIStore();
  }

  return (
    <AuthoringUIContext value={storeRef.current}>{children}</AuthoringUIContext>
  );
};

const useAuthoringUIStore = () => {
  const store = useContext(AuthoringUIContext);
  if (!store) {
    throw new Error("AuthoringUIContext is not defined");
  }
  return store;
};

export const useAuthoringUIActions = () => {
  const store = useAuthoringUIStore();
  return useStore(store, (state) => state.actions);
};

export const useSelectedResultsRowIndex = () => {
  const store = useAuthoringUIStore();
  return useStore(store, (state) =>
    state.selectedResultsRow ? state.selectedResultsRow.index : null,
  );
};

export const useSelectedResultsRow = () => {
  const store = useAuthoringUIStore();
  return useStore(
    store,
    useShallow((state) => ({
      selectedResultsRow: state.selectedResultsRow,
      selectedResultsRowNodeId: state.selectedResultsRowNodeId,
    })),
  );
};

export const useIsSelectedResultsRow = (index: number) => {
  const store = useAuthoringUIStore();
  return useStore(store, (state) => state.selectedResultsRow?.index === index);
};

export const useEdgeIsPartOfSelectedResultsRow = (
  sourceNodeId?: string,
  targetNodeId?: string,
): boolean => {
  const store = useAuthoringUIStore();
  return useStore(store, (state) => {
    if (!sourceNodeId || !targetNodeId) {
      return false;
    }
    if (!state.selectedResultsRow) {
      return false;
    }
    const executedNodeIds = Object.keys(
      state.selectedResultsRow.nodeExecutionMetadata ?? {},
    );
    return (
      executedNodeIds.includes(sourceNodeId) &&
      executedNodeIds.includes(targetNodeId)
    );
  });
};

export const useSelectedResultsRowNodeExecutionMetadata = () => {
  const store = useAuthoringUIStore();
  return useStore(store, (state) =>
    state.selectedResultsRow
      ? state.selectedResultsRow.nodeExecutionMetadata
      : null,
  );
};

export const useSelectedResultsRowDecisionId = () => {
  const store = useAuthoringUIStore();
  return useStore(store, (state) =>
    state.selectedResultsRow ? state.selectedResultsRow.decisionId : null,
  );
};

export const useNodeWasExecuted = (nodeId?: string) => {
  const store = useAuthoringUIStore();
  return useStore(store, (state) =>
    Boolean(
      nodeId && state.selectedResultsRow?.nodeExecutionMetadata?.[nodeId],
    ),
  );
};

export const useIsNodeEditorOpen = () => {
  const store = useAuthoringUIStore();
  return useStore(store, (state) => state.isNodeEditorOpen);
};
